import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function Sidebar() {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/employees', label: 'Employees', icon: '👥', roles: ['super_admin', 'admin_head', 'hr_head', 'accounts_head'] },
    { path: '/time-allocation', label: 'Time Allocation', icon: '📈', roles: ['super_admin', 'hr_head'] },
    { path: '/reports', label: 'Reports', icon: '📊', roles: ['super_admin', 'hr_head'] },
    { path: '/assets', label: 'Assets', icon: '💻', roles: ['super_admin', 'admin_head', 'hr_head', 'accounts_head'] },
    { path: "/audit-logs", label: "Audit Logs", icon: "📋", roles: ["super_admin", "hr_head"] },
  ];

  const getProfilePath = () => {
    return `/employees/${user?.id}`;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button className="mobile-menu-toggle" onClick={toggleSidebar}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>

      {/* Overlay for mobile */}
      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(false)}></div>

      <aside className={`sidebar sidebar-modern ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <Link to="/dashboard" className="sidebar-brand" onClick={() => setIsOpen(false)} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="logo-icon">S</div>
          {!isCollapsed && (
            <div className="sidebar-brand-text">
              <span className="brand-name">Sodel</span>
              <span className="brand-subtitle">HRMS</span>
            </div>
          )}
        </Link>

        <div className="sidebar-divider"></div>

        <div className="sidebar-user">
          <div className="sidebar-user-info">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name}
                className="sidebar-user-avatar"
              />
            ) : (
              <div className="sidebar-user-avatar-placeholder">
                {getInitials(user?.name || 'U')}
              </div>
            )}
            {!isCollapsed && (
              <div className="sidebar-user-details">
                <div className="sidebar-user-name">{user?.name}</div>
              </div>
            )}
          </div>
          {!isCollapsed && (
            <NavLink
              to={getProfilePath()}
              className={({ isActive }) => 
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setIsOpen(false)}
              style={{ marginTop: '12px' }}
            >
              <span className="nav-icon">👤</span>
              <span className="nav-label">My Profile</span>
            </NavLink>
          )}
        </div>

        <nav className="sidebar-nav">
          {/* Debug: User role: {user?.role} */}
          {navItems.filter(item => !item.roles || hasRole(...item.roles)).map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/employees'}
              className={({ isActive }) => 
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setIsOpen(false)}
              title={isCollapsed ? item.label : ''}
            >
              <span className="nav-icon">{item.icon}</span>
              {!isCollapsed && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          {!isCollapsed && (
            <button className="btn-logout" onClick={logout}>
              <span className="logout-icon">→</span>
              Logout
            </button>
          )}
          <button 
            className="btn-collapse" 
            onClick={toggleCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ 
              position: 'absolute',
              right: '-12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '12px',
              height: '24px',
              borderRadius: '0 4px 4px 0',
              backgroundColor: '#2c3e50',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px'
            }}
          >
            {isCollapsed ? '▶' : '◀'}
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;

